import { CreditReason, PaymentMethod, type Statement } from '@morales/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api, ApiError, newIdempotencyKey } from '../api';
import { useAuth } from '../auth';
import { EmailPanel } from '../components/EmailPanel';
import { Empty, ErrorBox, PageHead, PracticeBanner, QueryView } from '../components/ui';
import { formatDate, formatMoney, formatStamp, todayIso } from '../format';
import { useLocale } from '../i18n';
import type { AppMessageKey } from '../messages';
import { qk } from '../queryClient';
import { parseNumber } from './StockPage';
import { canKeepBooks } from './InvoicePage';

/** The customer's money: balance since the ledger started, open invoices with days late, payments, credits. */
export function StatementPage() {
  const { t, intlLocale } = useLocale();
  const { me } = useAuth();
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const qc = useQueryClient();
  const books = canKeepBooks(me?.role);
  // the Practice view (decision 187): practice invoices and money beside the real ones while QuickBooks holds the books
  const practice = params.get('practice') === '1';
  const query = useQuery({ queryKey: qk.statement(id, practice), queryFn: ({ signal }) => api.statement(id, signal, practice) });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['customers', 'statement', id] });
    void qc.invalidateQueries({ queryKey: ['invoices'] });
    void qc.invalidateQueries({ queryKey: ['customers'] });
  };

  return (
    <QueryView query={query}>
      {(s: Statement) => (
        <div className="stack">
          <PageHead title={`${t('statement')}: ${s.customerName}`} back={`/customers/${id}`} />
          {s.quickbooksHolds ? (
            <>
              <div className="chips" role="group" aria-label={t('statementView')} data-testid="statement-view">
                <Link className="chip" aria-pressed={!s.practice} to={`/customers/${id}/statement`} replace>
                  {t('statementReal')}
                </Link>
                <Link className="chip" aria-pressed={s.practice} to={`/customers/${id}/statement?practice=1${params.get('invoice') ? `&invoice=${params.get('invoice')}` : ''}`} replace>
                  {t('statementPractice')}
                </Link>
              </div>
              {s.practice ? <PracticeBanner /> : <p className="note" data-testid="real-in-quickbooks">{t('statementRealHint')}</p>}
            </>
          ) : null}

          {s.balanceFrom === null ? (
            <section className="card stack-sm">
              <p className="muted">{t('ledgerNotStarted')}</p>
              {me?.role === 'owner' ? <StartLedger onDone={refresh} /> : null}
            </section>
          ) : (
            <section className="card balance-card">
              {s.aging ? (
                <dl className="facts plain" data-testid="aging">
                  {(['current', 'd1to30', 'd31to60', 'd61to90', 'd90plus'] as const)
                    .filter((b) => s.aging![b] > 0)
                    .map((b) => (
                      <div key={b} className="fact">
                        <dt>{t(`bucket_${b}`)}</dt>
                        <dd>{formatMoney(s.aging![b], intlLocale)}</dd>
                      </div>
                    ))}
                  {/* money on account takes away from what the open invoices add up to (the balance below) */}
                  {s.toApply > 0.004 ? (
                    <div className="fact" data-testid="aging-on-account">
                      <dt>{t('onAccountLine')}</dt>
                      <dd>−{formatMoney(s.toApply, intlLocale)}</dd>
                    </div>
                  ) : null}
                  {s.creditLimit !== null ? (
                    <div className="fact">
                      <dt>{t('creditLimit')}</dt>
                      <dd>{formatMoney(s.creditLimit, intlLocale)}</dd>
                    </div>
                  ) : null}
                </dl>
              ) : null}
              <span className="muted">{t('balance')}</span>
              <strong className={s.balance !== null && s.balance > 0 ? 'owed' : undefined}>{s.balance === null ? '—' : formatMoney(s.balance, intlLocale)}</strong>
            </section>
          )}

          {books && s.openInvoices.some((o) => o.daysLate > 0) ? (
            <Link to={`/customers/${id}/paca-letter`} className="btn btn-secondary btn-block">
              {t('pacaLetter')}
            </Link>
          ) : null}
          <section className="card stack-sm">
            <h2>{t('openInvoices')}</h2>
            {s.opening !== 0 && s.balanceFrom ? (
              <p className="row" data-testid="opening-line">
                <span className="row-main">
                  <span className="row-title">{t('openingLine').replace('{date}', formatDate(s.balanceFrom, intlLocale, 'date'))}</span>
                </span>
                <span className="row-end">
                  <span className="row-end-value">{formatMoney(s.opening, intlLocale)}</span>
                </span>
              </p>
            ) : null}
            {s.openInvoices.length === 0 ? (
              s.opening !== 0 ? null : <Empty>{t('noResults')}</Empty>
            ) : (
              <ul className="list">
                {s.openInvoices.map((o) => (
                  <li key={o.id}>
                    <Link to={`/invoices/${o.id}`} className="row">
                      <span className="row-main">
                        <span className="row-title">
                          {t('invoice')} {o.invoiceNo}
                          {o.test ? <span className="badge badge-warn"> {t('testTag')}</span> : null}
                        </span>
                        <span className="row-sub">
                          {formatDate(o.date, intlLocale, 'date')}
                          {o.daysLate > 0 ? <span className="badge badge-warn">{o.daysLate} {t('daysLate')}</span> : <span className="badge">{t('onTime')}</span>}
                        </span>
                      </span>
                      <span className="row-end">
                        <span className="row-end-label">{t('balanceDue')}</span>
                        <span className="row-end-value">{formatMoney(o.balance, intlLocale)}</span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* while QuickBooks holds the books real money is entered there: the forms are for practice */}
          {books && s.balanceFrom !== null && (!s.quickbooksHolds || s.practice) ? (
            <>
              <ApplyOnAccountCard statement={s} onDone={refresh} />
              <PaymentForm statement={s} presetInvoice={params.get('invoice')} onDone={refresh} />
              <CreditForm statement={s} onDone={refresh} />
            </>
          ) : null}

          <section className="card stack-sm">
            <h2>{t('payments')}</h2>
            {s.payments.length === 0 ? (
              <Empty>{t('noResults')}</Empty>
            ) : (
              <ul className="list">
                {s.payments.map((p) => (
                  <li key={p.id} className="row">
                    <span className="row-main">
                      <span className="row-title">
                        {t(`method_${p.method}`)}
                        {p.reference ? ` · ${p.reference}` : ''}
                      </span>
                      <span className="row-sub">
                        {formatStamp(p.receivedAt, intlLocale, 'date')}
                        {p.invoiceNo ? ` · ${t('invoice')} ${p.invoiceNo}` : ` · ${t('onAccount')}`}
                        {p.note ? ` · ${p.note}` : ''}
                      </span>
                    </span>
                    <span className="row-end">
                      <span className={p.amount < 0 ? 'row-end-value owed' : 'row-end-value'}>{formatMoney(p.amount, intlLocale)}</span>
                      {books && (!s.quickbooksHolds || (s.practice && p.test)) && p.amount > 0 && !p.reversesPaymentId && !s.payments.some((x) => x.reversesPaymentId === p.id) && firstOfGroup(s.payments, p) ? (
                        <ReverseButton paymentId={p.id} lines={p.groupId ? s.payments.filter((x) => x.groupId === p.groupId && !x.reversesPaymentId).length : 1} onDone={refresh} />
                      ) : null}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="card stack-sm">
            <h2>{t('credits')}</h2>
            {s.credits.length === 0 ? (
              <Empty>{t('noResults')}</Empty>
            ) : (
              <ul className="list">
                {s.credits.map((c) => (
                  <li key={c.id} className="row" data-testid="credit-row">
                    <span className="row-main">
                      <span className="row-title">
                        {t(`reason_${c.reason}`)}
                        {c.reversed ? <span className="badge badge-warn">{t('creditUndone')}</span> : null}
                        {c.reversesCreditId ? <span className="badge">{t('creditUndoes')}</span> : null}
                      </span>
                      <span className="row-sub">
                        {formatStamp(c.createdAt, intlLocale, 'date')}
                        {c.invoiceNo ? ` · ${t('invoice')} ${c.invoiceNo}` : ''}
                        {c.reasonText ? ` · ${c.reasonText}` : ''}
                      </span>
                      {/* a credit given by mistake is undone, once, with why (decision 184) */}
                      {books && (!s.quickbooksHolds || (s.practice && c.test)) && !c.reversed && !c.reversesCreditId ? <UndoCreditButton creditId={c.id} onDone={refresh} /> : null}
                    </span>
                    <span className="row-end-value">{formatMoney(c.amount, intlLocale)}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
          {books && s.balanceFrom !== null ? <EmailPanel target={{ kind: 'statement', customerId: s.customerId }} customerId={s.customerId} /> : null}
        </div>
      )}
    </QueryView>
  );
}

function StartLedger({ onDone }: { onDone: () => void }) {
  const { t } = useLocale();
  const [date, setDate] = useState(todayIso());
  const start = useMutation({ mutationFn: (d: string) => api.startLedger(d), onSuccess: onDone });
  return (
    <form
      className="picker-head"
      onSubmit={(e) => {
        e.preventDefault();
        start.mutate(date);
      }}
    >
      <label className="field" style={{ flex: 1 }}>
        <span>{t('startLedger')}</span>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
      </label>
      <button type="submit" className="btn btn-primary" disabled={start.isPending}>
        {t('save')}
      </button>
      {start.error ? <ErrorBox error={start.error} /> : null}
    </form>
  );
}

/**
 * Spread an amount over the open invoices, oldest first (the preset invoice first when there is one). What is still owed
 * from before the ledger start is the oldest debt: that much is kept off the invoices (left on account, which pays the
 * opening balance first), unless the payment was opened for one invoice.
 */
export function allocate(amount: number, open: { id: string; balance: number }[], firstId?: string | null, openingOwed = 0): Record<string, string> {
  const order = firstId ? [...open.filter((o) => o.id === firstId), ...open.filter((o) => o.id !== firstId)] : open;
  let left = Math.round(amount * 100) - (firstId ? 0 : Math.min(Math.round(amount * 100), Math.round(openingOwed * 100)));
  const out: Record<string, string> = {};
  for (const o of order) {
    const take = Math.max(0, Math.min(left, Math.round(o.balance * 100)));
    out[o.id] = take > 0 ? (take / 100).toFixed(2) : '';
    left -= take;
  }
  return out;
}

export function PaymentForm({ statement, presetInvoice, onDone }: { statement: Statement; presetInvoice: string | null; onDone: () => void }) {
  const { t, intlLocale } = useLocale();
  const open = statement.openInvoices.filter((o) => o.balance > 0);
  const preset = open.find((o) => o.id === presetInvoice);
  const [amount, setAmount] = useState(preset ? String(preset.balance) : '');
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [reference, setReference] = useState('');
  // what goes on each invoice: filled oldest first from the amount until a cell is typed in by hand
  const [split, setSplit] = useState<Record<string, string>>(() => (preset ? allocate(preset.balance, open, preset.id) : {}));
  const [typed, setTyped] = useState(false);
  const [problem, setProblem] = useState<AppMessageKey | null>(null);
  const [saved, setSaved] = useState(false);
  const attempt = useRef<{ body: string; key: string } | null>(null);
  const save = useMutation({
    mutationFn: (v: { body: Parameters<typeof api.receivePayment>[0]; key: string }) => api.receivePayment(v.body, v.key),
    retry: (count, error) => error instanceof ApiError && error.status === 0 && count < 3,
    onSuccess: () => {
      attempt.current = null;
      setAmount('');
      setReference('');
      setSplit({});
      setTyped(false);
      setSaved(true);
      onDone();
    },
    onError: (error) => {
      if (!(error instanceof ApiError && error.status === 0)) attempt.current = null;
    },
  });
  const received = parseNumber(amount);
  const cells = open.map((o) => ({ ...o, raw: split[o.id] ?? '', value: (split[o.id] ?? '').trim() === '' ? 0 : parseNumber(split[o.id]!) }));
  const appliedCents = cells.reduce((n, c) => n + Math.round((c.value ?? 0) * 100), 0);
  const leftCents = received === null ? 0 : Math.round(received * 100) - appliedCents;
  function changeAmount(v: string) {
    setAmount(v);
    const n = parseNumber(v);
    if (!typed) setSplit(n === null ? {} : allocate(n, open, preset?.id, statement.openingOwed));
  }
  function submit() {
    if (received === null || received <= 0) return setProblem('error');
    if (cells.some((c) => c.value === null || c.value < 0)) return setProblem('typedNotNumber');
    if (cells.some((c) => c.value! > c.balance + 0.004)) return setProblem('moreThanOwes');
    if (leftCents < 0) return setProblem('moreThanReceived');
    setProblem(null);
    setSaved(false);
    const applications = cells.filter((c) => (c.value ?? 0) > 0).map((c) => ({ invoiceId: c.id, amount: Math.round(c.value! * 100) / 100 }));
    const body = { customerId: statement.customerId, amount: Math.round(received * 100) / 100, method, ...(reference.trim() ? { reference: reference.trim() } : {}), applications };
    const serialized = JSON.stringify(body);
    if (attempt.current?.body !== serialized) attempt.current = { body: serialized, key: newIdempotencyKey() };
    save.mutate({ body, key: attempt.current.key });
  }
  return (
    <section className="card stack-sm">
      <h2>{t('recordPayment')}</h2>
      {saved ? (
        <div className="flash" role="status">
          ✓ {t('paymentSaved')}
        </div>
      ) : null}
      <form
        className="stack-sm"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div className="picker-head">
          <label className="field" style={{ flex: 1 }}>
            <span>{t('amount')}</span>
            <input type="text" inputMode="decimal" autoComplete="off" value={amount} onChange={(e) => changeAmount(e.target.value)} placeholder="0.00" />
          </label>
          <label className="field">
            <span>{t('method')}</span>
            <select value={method} onChange={(e) => setMethod(PaymentMethod.parse(e.target.value))}>
              {PaymentMethod.options.map((m) => (
                <option key={m} value={m}>
                  {t(`method_${m}`)}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="field">
          <span>{t('reference')}</span>
          <input type="text" autoComplete="off" value={reference} onChange={(e) => setReference(e.target.value)} />
        </label>
        {open.length > 0 ? (
          <div className="table-wrap">
            <table className="table" data-testid="payment-split">
              <caption className="muted" style={{ textAlign: 'left' }}>{t('paymentInvoices')}</caption>
              <thead>
                <tr>
                  <th>{t('invoice')}</th>
                  <th className="num">{t('balanceDue')}</th>
                  <th className="num">{t('paymentCol')}</th>
                </tr>
              </thead>
              <tbody>
                {cells.map((c) => (
                  <tr key={c.id}>
                    <td>
                      {c.invoiceNo} <span className="muted">· {formatDate(c.date, intlLocale, 'date')}</span>
                      {c.daysLate > 0 ? <span className="badge badge-warn">{c.daysLate} {t('daysLate')}</span> : null}
                    </td>
                    <td className="num">{formatMoney(c.balance, intlLocale)}</td>
                    <td className="num">
                      <input
                        type="text"
                        inputMode="decimal"
                        autoComplete="off"
                        className="num"
                        aria-invalid={c.value === null || c.value < 0 || c.value > c.balance + 0.004}
                        style={{ width: '7rem' }}
                        value={c.raw}
                        aria-label={`${t('paymentCol')}: ${t('invoice')} ${c.invoiceNo}`}
                        onChange={(e) => {
                          setTyped(true);
                          setSplit({ ...split, [c.id]: e.target.value });
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={2}>{t('appliedTotal')}</td>
                  <td className="num" data-testid="applied-total">{formatMoney(appliedCents / 100, intlLocale)}</td>
                </tr>
                <tr>
                  <td colSpan={2}>{t('leftOnAccount')}</td>
                  <td className={leftCents < 0 ? 'num owed' : 'num'} data-testid="left-on-account">{formatMoney(leftCents / 100, intlLocale)}</td>
                </tr>
                {/* money left on account pays what was owed before the ledger start first (the oldest debt) */}
                {statement.openingOwed > 0 && leftCents > 0 ? (
                  <tr>
                    <td colSpan={3} className="muted small" data-testid="pays-opening">
                      {t('paysOpening').replace('{amount}', formatMoney(Math.min(leftCents / 100, statement.openingOwed), intlLocale)).replace('{owed}', formatMoney(statement.openingOwed, intlLocale))}
                    </td>
                  </tr>
                ) : null}
              </tfoot>
            </table>
          </div>
        ) : (
          <p className="muted">{t('onAccount')}</p>
        )}
        {problem ? (
          <p className="field-error" role="alert">
            {t(problem)}
          </p>
        ) : null}
        {save.error ? <ErrorBox error={save.error} /> : null}
        <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={save.isPending}>
          {save.isPending ? t('sending') : t('recordPayment')}
        </button>
      </form>
    </section>
  );
}

/** Practice money on account goes on the practice invoices, oldest due first (as the server would), up to what there is. */
function practiceTargets(s: Statement): { invoiceId: string; amount: number }[] {
  let left = Math.round(s.toApply * 100);
  const out: { invoiceId: string; amount: number }[] = [];
  for (const o of s.openInvoices) {
    if (left <= 0) break;
    if (!o.test || o.balance <= 0) continue;
    const take = Math.min(left, Math.round(o.balance * 100));
    out.push({ invoiceId: o.id, amount: take / 100 });
    left -= take;
  }
  return out;
}

/** Money on account (paid ahead, a credit, an overpayment) put on the oldest invoices in one tap. */
function ApplyOnAccountCard({ statement, onDone }: { statement: Statement; onDone: () => void }) {
  const { t, intlLocale } = useLocale();
  const [saved, setSaved] = useState(false);
  const attempt = useRef<string | null>(null);
  const apply = useMutation({
    mutationFn: () => {
      attempt.current ??= newIdempotencyKey();
      return api.applyOnAccount(statement.customerId, statement.practice ? { applications: practiceTargets(statement) } : {}, attempt.current);
    },
    retry: (count, error) => error instanceof ApiError && error.status === 0 && count < 3,
    onSuccess: () => {
      attempt.current = null;
      setSaved(true);
      onDone();
    },
    onError: (error) => {
      if (!(error instanceof ApiError && error.status === 0)) attempt.current = null;
    },
  });
  if (saved && statement.toApply <= 0) {
    return (
      <div className="flash" role="status">
        ✓ {t('appliedSaved')}
      </div>
    );
  }
  // in the Practice view practice money goes only on practice invoices (decision 187)
  if (statement.toApply <= 0 || !statement.openInvoices.some((o) => o.balance > 0 && (!statement.practice || o.test))) return null;
  return (
    <section className="card stack-sm" data-testid="apply-on-account">
      <p>{t('applyOnAccountHint').replace('{amount}', formatMoney(statement.toApply, intlLocale))}</p>
      {apply.error ? <ErrorBox error={apply.error} /> : null}
      <button type="button" className="btn btn-secondary btn-block" disabled={apply.isPending} onClick={() => apply.mutate()}>
        {t('applyOnAccount')}
      </button>
    </section>
  );
}

function CreditForm({ statement, onDone }: { statement: Statement; onDone: () => void }) {
  const { t, intlLocale } = useLocale();
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState<Statement['credits'][number]['reason']>('quality');
  const [text, setText] = useState('');
  const [invoiceId, setInvoiceId] = useState('');
  const [saved, setSaved] = useState(false);
  // one key per credit: a retried tap (a dropped connection) never credits twice
  const creditAttempt = useRef<{ body: string; key: string } | null>(null);
  const save = useMutation({
    mutationFn: () => {
      const n = parseNumber(amount) ?? 0;
      const body = { customerId: statement.customerId, amount: Math.round(n * 100) / 100, reason, ...(text.trim() ? { reasonText: text.trim() } : {}), ...(invoiceId ? { invoiceId } : {}) };
      const serialized = JSON.stringify(body);
      if (creditAttempt.current?.body !== serialized) creditAttempt.current = { body: serialized, key: newIdempotencyKey() };
      return api.giveCredit(body, creditAttempt.current.key);
    },
    onSuccess: () => {
      creditAttempt.current = null;
      setAmount('');
      setText('');
      setInvoiceId('');
      setSaved(true);
      onDone();
    },
  });
  const n = parseNumber(amount);
  return (
    <section className="card stack-sm">
      <h2>{t('giveCredit')}</h2>
      {saved ? (
        <div className="flash" role="status">
          ✓ {t('creditSaved')}
        </div>
      ) : null}
      <form
        className="stack-sm"
        onSubmit={(e) => {
          e.preventDefault();
          if (n !== null && n > 0) save.mutate();
        }}
      >
        <div className="picker-head">
          <label className="field" style={{ flex: 1 }}>
            <span>{t('amount')}</span>
            <input type="text" inputMode="decimal" autoComplete="off" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" aria-label={`${t('giveCredit')}: ${t('amount')}`} />
          </label>
          <label className="field">
            <span>{t('creditReason')}</span>
            <select value={reason} onChange={(e) => setReason(CreditReason.parse(e.target.value))}>
              {CreditReason.options.map((r) => (
                <option key={r} value={r}>
                  {t(`reason_${r}`)}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="field">
          <span>{t('notes')}</span>
          <input type="text" autoComplete="off" value={text} onChange={(e) => setText(e.target.value)} />
        </label>
        <label className="field">
          <span>{t('applyTo')}</span>
          <select value={invoiceId} onChange={(e) => setInvoiceId(e.target.value)} aria-label={`${t('giveCredit')}: ${t('applyTo')}`}>
            <option value="">{t('onAccount')}</option>
            {statement.openInvoices.map((o) => (
              <option key={o.id} value={o.id}>
                {t('invoice')} {o.invoiceNo} · {formatMoney(o.balance, intlLocale)}
              </option>
            ))}
          </select>
        </label>
        {save.error ? <ErrorBox error={save.error} /> : null}
        <button type="submit" className="btn btn-secondary btn-lg btn-block" disabled={save.isPending || n === null || n <= 0}>
          {t('giveCredit')}
        </button>
      </form>
    </section>
  );
}

/** One check over several invoices reverses as one: its Reverse sits on its first line paid in. */
function firstOfGroup(all: Statement['payments'], p: Statement['payments'][number]): boolean {
  if (!p.groupId) return true;
  return all.find((x) => x.groupId === p.groupId && x.amount > 0 && !x.reversesPaymentId)?.id === p.id;
}

function UndoCreditButton({ creditId, onDone }: { creditId: string; onDone: () => void }) {
  const { t } = useLocale();
  const [asking, setAsking] = useState(false);
  const [note, setNote] = useState('');
  const undo = useMutation({ mutationFn: () => api.reverseCredit(creditId, note.trim()), onSuccess: () => { setAsking(false); onDone(); } });
  if (!asking) {
    return (
      <button type="button" className="btn btn-ghost" onClick={() => setAsking(true)}>
        {t('undoCredit')}
      </button>
    );
  }
  return (
    <form className="stack-sm" onSubmit={(e) => { e.preventDefault(); if (note.trim()) undo.mutate(); }}>
      <input type="text" className="input-wide" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} placeholder={t('undoCreditWhy')} aria-label={t('undoCreditWhy')} autoFocus />
      <button type="submit" className="btn btn-secondary" disabled={undo.isPending || !note.trim()}>
        {t('undoCredit')}
      </button>
      {undo.error ? <ErrorBox error={undo.error} /> : null}
    </form>
  );
}

function ReverseButton({ paymentId, lines, onDone }: { paymentId: string; lines: number; onDone: () => void }) {
  const { t } = useLocale();
  const [asking, setAsking] = useState(false);
  const [note, setNote] = useState('');
  const reverse = useMutation({
    mutationFn: () => api.reversePayment(paymentId, note.trim()),
    onSuccess: () => {
      setAsking(false);
      onDone();
    },
  });
  if (!asking) {
    return (
      <button type="button" className="btn btn-ghost" onClick={() => setAsking(true)}>
        {t('reverse')}
      </button>
    );
  }
  return (
    <form
      className="stack-sm"
      onSubmit={(e) => {
        e.preventDefault();
        if (note.trim()) reverse.mutate();
      }}
    >
      {lines > 1 ? <p className="muted">{t('reverseGroup').replace('{n}', String(lines))}</p> : null}
      <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('reverseWhy')} aria-label={t('reverseWhy')} autoFocus />
      <button type="submit" className="btn btn-secondary" disabled={reverse.isPending || !note.trim()}>
        {t('reverse')}
      </button>
      {reverse.error ? <ErrorBox error={reverse.error} /> : null}
    </form>
  );
}
